import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { PasswordInput } from "@/components/ui/password-input";
import { Banner } from "@/components/ui/banner";
import {
  forcedPasswordChangeSchema,
  type ForcedPasswordChangeValues,
} from "@/routes/forced-password-change/forced-password-change-schema";
import { toForcedChangeError } from "@/routes/forced-password-change/password-error";
import type { AuthBanner } from "@/routes/sign-in/auth-error";

// First-login forced password change (FR56a / Story 2.7). The user arrives here from
// the Story 2.9 guards because their JWT carries force_password_change: true; this
// route sits under <RequireAuth> only (NOT ProtectedLayout) so the redirect cannot
// loop onto itself.
//
// Flow: POST /auth/change-password { current_password, new_password } → 200 returns a
// FRESH token pair (new access_token has force_password_change: false) → setSession
// flips the auth-context flag → navigate("/") lets HomeRedirect route by role. We
// never re-derive routing here.
//
// skipAuthRefresh: true is REQUIRED — a wrong-current-password 401 AUTH-001 would
// otherwise be swallowed by the silent-refresh→retry path and log the user out
// instead of surfacing inline. See story Dev Notes "Why change-password must skip
// auth-refresh".
//
// Secrets (current/new/confirm passwords, access_token) live in form/closure state
// only — never logged, never in error text, never persisted.

interface ChangePasswordResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  email: string;
  role: string;
  org_id: string | null;
}

export function ForcedPasswordChangeScreen() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [banner, setBanner] = React.useState<AuthBanner | null>(null);

  const form = useForm<ForcedPasswordChangeValues>({
    resolver: zodResolver(forcedPasswordChangeSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  const changeMutation = useMutation({
    mutationFn: (values: ForcedPasswordChangeValues) =>
      apiFetch<ChangePasswordResponse>("/auth/change-password", {
        method: "POST",
        body: {
          current_password: values.currentPassword,
          new_password: values.newPassword,
        },
        // REQUIRED: keep a 401 AUTH-001 (wrong current password) as an inline error
        // instead of triggering a refresh→retry that would log the user out.
        skipAuthRefresh: true,
      }),
    onSuccess: async (data) => {
      setBanner(null);
      // Adopt the fresh token (force_password_change: false) in-memory only, then let
      // the guards route by role — never hardcode the landing route.
      await setSession(data.access_token);
      navigate("/", { replace: true });
    },
    onError: (error) => {
      const mapped = toForcedChangeError(error);
      if (mapped.fieldName && mapped.fieldMessage) {
        form.setError(mapped.fieldName, { message: mapped.fieldMessage });
      }
      setBanner(mapped.banner);
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (changeMutation.isPending) return;
    setBanner(null);
    changeMutation.mutate(values);
  });

  return (
    <main className="flex min-h-screen items-center justify-center p-5">
      <Card className="w-[min(var(--width-sm),100%)] shadow-elevated">
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
          <CardDescription>
            Choose a new password before continuing to your workspace.
          </CardDescription>
        </CardHeader>

        {banner ? (
          <Banner variant={banner.variant} className="mt-4">
            {banner.message}
          </Banner>
        ) : null}

        <form className="mt-5 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <FormField
            label="Current password"
            error={form.formState.errors.currentPassword?.message}
            required
          >
            <PasswordInput
              autoComplete="current-password"
              {...form.register("currentPassword")}
            />
          </FormField>

          <FormField
            label="New password"
            error={form.formState.errors.newPassword?.message}
            helper="Minimum 8 characters."
            required
          >
            <PasswordInput autoComplete="new-password" {...form.register("newPassword")} />
          </FormField>

          <FormField
            label="Confirm new password"
            error={form.formState.errors.confirmPassword?.message}
            required
          >
            <PasswordInput autoComplete="new-password" {...form.register("confirmPassword")} />
          </FormField>

          <Button type="submit" variant="primary" disabled={changeMutation.isPending}>
            {changeMutation.isPending ? "Updating password…" : "Update password and continue"}
          </Button>
        </form>
      </Card>
    </main>
  );
}

export default ForcedPasswordChangeScreen;
